import {
  NotificationOptions,
  DateFormatOptions,
  CurrencyFormatOptions,
  NumberFormatOptions,
  PhoneFormatOptions,
  DebouncedFunction,
  ThrottledFunction
} from '../types/utils';

/**
 * Utilitários para a aplicação ConfereAí
 * Funções auxiliares e helpers
 */
export class Utils {
  /**
   * Formata data para exibição
   */
  static formatDate(date: string | Date | null | undefined, options: DateFormatOptions = {}): string {
    if (!date) return '-';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    
    const format = options.format || 'dd/MM/yyyy';
    
    switch (format) {
      case 'dd/MM/yyyy':
        return `${day}/${month}/${year}`;
      case 'dd/MM/yyyy HH:mm':
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      case 'yyyy-MM-dd':
        return `${year}-${month}-${day}`;
      case 'HH:mm':
        return `${hours}:${minutes}`;
      default:
        return d.toLocaleDateString('pt-BR');
    }
  }

  /**
   * Formata número para moeda brasileira
   */
  static formatCurrency(value: number | null | undefined, options: CurrencyFormatOptions = {}): string {
    if (value === null || value === undefined) return 'R$ 0,00';
    
    const decimals = options.decimals ?? 2;
    const currency = options.currency ?? 'BRL';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  }

  /**
   * Formata número com separadores de milhar
   */
  static formatNumber(value: number | null | undefined, options: NumberFormatOptions = {}): string {
    if (value === null || value === undefined) return '0';
    
    const decimals = options.decimals ?? 0;
    
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  }

  /**
   * Calcula diferença entre duas datas em dias
   */
  static dateDiffInDays(date1: Date, date2: Date): number {
    const msPerDay = 1000 * 60 * 60 * 24;
    const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return Math.floor((utc2 - utc1) / msPerDay);
  }

  /**
   * Verifica se uma data é válida
   */
  static isValidDate(date: string | Date): boolean {
    const d = new Date(date);
    return d instanceof Date && !isNaN(d.getTime());
  }

  /**
   * Debounce function para otimizar eventos
   */
  static debounce<T extends (...args: any[]) => any>(func: T, wait: number): DebouncedFunction<T> {
    let timeout: NodeJS.Timeout | null = null;
    
    const debounced = (...args: Parameters<T>) => {
      if (timeout) {
        clearTimeout(timeout);
      }
      
      timeout = setTimeout(() => {
        func(...args);
      }, wait);
    };
    
    debounced.cancel = () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    };
    
    return debounced;
  }

  /**
   * Throttle function para limitar execução
   */
  static throttle<T extends (...args: any[]) => any>(func: T, limit: number): ThrottledFunction<T> {
    let inThrottle = false;
    
    const throttled = (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => {
          inThrottle = false;
        }, limit);
      }
    };
    
    throttled.cancel = () => {
      inThrottle = false;
    };
    
    return throttled;
  }

  /**
   * Deep clone de objeto
   */
  static deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as T;
    if (Array.isArray(obj)) return obj.map(item => this.deepClone(item)) as T;
    
    const cloned = {} as T;
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        (cloned as any)[key] = this.deepClone((obj as any)[key]);
      }
    }
    return cloned;
  }

  /**
   * Mescla dois objetos profundamente
   */
  static deepMerge<T extends object, U extends object>(target: T, source: U): T & U {
    const output = { ...target } as T & U;
    
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        const sourceKey = key as keyof U;
        const sourceValue = source[sourceKey];
        
        if (this.isObject(sourceValue)) {
          const targetValue = (target as any)[key];
          if (this.isObject(targetValue)) {
            (output as any)[key] = this.deepMerge(targetValue, sourceValue);
          } else {
            (output as any)[key] = sourceValue;
          }
        } else {
          (output as any)[key] = sourceValue;
        }
      });
    }
    
    return output;
  }

  /**
   * Verifica se é um objeto
   */
  static isObject(item: any): item is object {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  /**
   * Gera ID único
   */
  static generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Sanitiza string para prevenir XSS
   */
  static sanitize(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Trunca texto com elipse
   */
  static truncate(text: string, maxLength: number = 100): string {
    if (!text || text.length <= maxLength) return text;
    return text.substr(0, maxLength - 3) + '...';
  }

  /**
   * Capitaliza primeira letra
   */
  static capitalize(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /**
   * Converte string para slug
   */
  static slugify(str: string): string {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  /**
   * Valida email
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Valida CPF
   */
  static isValidCPF(cpf: string): boolean {
    cpf = cpf.replace(/[^\d]/g, '');
    
    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    
    let sum = 0;
    let remainder: number;
    
    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
    
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(9, 10))) return false;
    
    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
    
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(10, 11))) return false;
    
    return true;
  }

  /**
   * Valida CNPJ
   */
  static isValidCNPJ(cnpj: string): boolean {
    cnpj = cnpj.replace(/[^\d]/g, '');
    
    if (cnpj.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(cnpj)) return false;
    
    let size = cnpj.length - 2;
    let numbers = cnpj.substring(0, size);
    let digits = cnpj.substring(size);
    let sum = 0;
    let pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;
    
    size = size + 1;
    numbers = cnpj.substring(0, size);
    sum = 0;
    pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(1))) return false;
    
    return true;
  }

  /**
   * Formata CPF
   */
  static formatCPF(cpf: string): string {
    cpf = cpf.replace(/[^\d]/g, '');
    if (cpf.length !== 11) return cpf;
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  /**
   * Formata CNPJ
   */
  static formatCNPJ(cnpj: string): string {
    cnpj = cnpj.replace(/[^\d]/g, '');
    if (cnpj.length !== 14) return cnpj;
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  /**
   * Formata telefone
   */
  static formatPhone(phone: string, options: PhoneFormatOptions = {}): string {
    phone = phone.replace(/[^\d]/g, '');
    
    if (phone.length === 11) {
      return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (phone.length === 10) {
      return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return phone;
    }
  }

  /**
   * Converte bytes para formato legível
   */
  static formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
  }

  /**
   * Mostra notificação toast
   */
  static showNotification(message: string, options: NotificationOptions = {}): void {
    const type = options.type || 'info';
    const duration = options.duration || 5000;
    
    // Criar elemento de notificação
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.innerHTML = `
      <div class="notification-content">
        <span class="notification-icon">${this.getNotificationIcon(type)}</span>
        <span class="notification-message">${this.sanitize(message)}</span>
        <button class="notification-close" onclick="this.parentElement.parentElement.remove()">
          &times;
        </button>
      </div>
    `;
    
    // Adicionar estilos se não existirem
    if (!document.getElementById('notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'notification-styles';
      styles.textContent = `
        .notification {
          position: fixed;
          top: 20px;
          right: 20px;
          background: white;
          padding: 15px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
          z-index: 10000;
          animation: slideIn 0.3s ease-out;
          max-width: 400px;
          border-left: 4px solid;
        }
        
        .notification-success { border-left-color: #27ae60; }
        .notification-error { border-left-color: #e74c3c; }
        .notification-warning { border-left-color: #f39c12; }
        .notification-info { border-left-color: var(--color-primary); }
        
        .notification-content {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .notification-icon {
          font-size: 1.2rem;
        }
        
        .notification-message {
          flex: 1;
          color: #2c3e50;
        }
        
        .notification-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #7f8c8d;
          padding: 0;
          margin: 0;
        }
        
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `;
      document.head.appendChild(styles);
    }
    
    // Adicionar ao documento
    document.body.appendChild(notification);
    
    // Remover automaticamente após a duração
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, duration);
  }

  /**
   * Obtém ícone para notificação
   */
  private static getNotificationIcon(type: string): string {
    const icons: Record<string, string> = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };
    return icons[type] || 'ℹ️';
  }

  /**
   * Função para copiar texto para área de transferência
   */
  static async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      this.showNotification('Texto copiado para a área de transferência!', { type: 'success' });
      return true;
    } catch (error) {
      console.error('Erro ao copiar texto:', error);
      
      // Fallback para navegadores mais antigos
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          this.showNotification('Texto copiado para a área de transferência!', { type: 'success' });
          return true;
        }
      } catch (fallbackError) {
        console.error('Erro no fallback de cópia:', fallbackError);
      }
      
      this.showNotification('Não foi possível copiar o texto', { type: 'error' });
      return false;
    }
  }

  /**
   * Função para baixar conteúdo como arquivo
   */
  static downloadFile(content: string, filename: string, contentType: string = 'text/plain'): void {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    
    document.body.appendChild(a);
    a.click();
    
    // Limpar
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  }

  /**
   * Função para ler arquivo como texto
   */
  static readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      
      reader.readAsText(file);
    });
  }

  /**
   * Função para ler arquivo como Data URL
   */
  static readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Função para validar tamanho de arquivo
   */
  static validateFileSize(file: File, maxSizeMB: number = 10): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  /**
   * Função para validar tipo de arquivo
   */
  static validateFileType(file: File, allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/gif']): boolean {
    return allowedTypes.includes(file.type);
  }

  /**
   * Função para gerar cor baseada em string (hash)
   */
  static stringToColor(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xFF;
      color += ('00' + value.toString(16)).substr(-2);
    }
    
    return color;
  }

  /**
   * Função para obter iniciais de um nome
   */
  static getInitials(name: string): string {
    if (!name) return '?';
    
    const names = name.trim().split(/\s+/);
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  }

  /**
   * Função para gerar avatar com iniciais
   */
  static generateAvatar(name: string, size: number = 100): string {
    const initials = this.getInitials(name);
    const color = this.stringToColor(name);
    
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    
    const context = canvas.getContext('2d');
    if (!context) return '';
    
    // Fundo
    context.fillStyle = color;
    context.fillRect(0, 0, size, size);
    
    // Texto
    context.fillStyle = '#FFFFFF';
    context.font = `bold ${size * 0.4}px Arial, sans-serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(initials, size / 2, size / 2);
    
    return canvas.toDataURL();
  }

  /**
   * Função para detectar dispositivo móvel
   */
  static isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Função para detectar navegador
   */
  static getBrowser(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) return 'IE';
    
    return 'Unknown';
  }

  /**
   * Função para detectar sistema operacional
   */
  static getOS(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'MacOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';
    
    return 'Unknown';
  }

  /**
   * Função para verificar conexão com internet
   */
  static async isOnline(): Promise<boolean> {
    if (navigator.onLine === false) return false;
    
    try {
      const response = await fetch('https://httpbin.org/status/200', {
        method: 'HEAD',
        cache: 'no-store'
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Função para obter parâmetros da URL
   */
  static getUrlParam(name: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  /**
   * Função para definir parâmetro na URL
   */
  static setUrlParam(name: string, value: string): void {
    const url = new URL(window.location.href);
    url.searchParams.set(name, value);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Função para remover parâmetro da URL
   */
  static removeUrlParam(name: string): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(name);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Calcula tempo relativo (há x tempo)
   */
  static getTimeAgo(dateString: string): string {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours} h`;
    if (diffDays < 30) return `${diffDays} dias`;
    
    return this.formatDate(dateString, { format: 'dd/MM/yyyy' });
  }
}

// Exportar instância única
export const utils = Utils;

console.log('✅ Utilitários carregados');
